'use client'

import Link from '@/lib/react-compat/link'
import { Button } from '@/components/ui/button'
import { ChevronRight } from 'lucide-react'
import { useScrollAnimation } from '@/hooks/use-scroll-animation'
import { useI18n } from '@/components/i18n-provider'

type PanelField = { label: string; value: string }
type PanelCrop = { name: string; confidence: number }

const APPLE_EASE = 'ease-[cubic-bezier(0.28,0.11,0.32,1)]'

export function HeroSection() {
  const { t } = useI18n()
  const { elementRef: heroRef, isVisible: heroVisible } = useScrollAnimation()
  const { elementRef: panelRef, isVisible: panelVisible } = useScrollAnimation({ threshold: 0.2 })

  const panelFields = (t('hero.panel.fields') as unknown as PanelField[]) || []
  const panelCrops = (t('hero.panel.crops') as unknown as PanelCrop[]) || []

  return (
    <section className="relative overflow-hidden bg-background pb-24 pt-20 sm:pb-32 sm:pt-28">
      {/* 柔和的氛围光晕：极淡的绿色径向渐变，代替旧纹理 */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-[640px] bg-[radial-gradient(60%_50%_at_50%_0%,rgba(21,128,61,0.08),transparent_70%)] dark:bg-[radial-gradient(60%_50%_at_50%_0%,rgba(74,222,128,0.07),transparent_70%)]"
      />

      <div className="container relative z-10">
        {/* Centered copy — Apple product-page rhythm */}
        <div
          ref={heroRef}
          className={`mx-auto max-w-3xl text-center transition-all duration-700 ${APPLE_EASE} ${
            heroVisible ? 'translate-y-0 opacity-100' : 'translate-y-5 opacity-0'
          }`}
        >
          <h1 className="text-balance text-5xl font-semibold leading-[1.04] tracking-[-0.025em] text-foreground sm:text-6xl lg:text-7xl">
            {t('hero.titleLeading')}
            <br />
            <span className="text-green-700 dark:text-green-400">{t('hero.titleEmphasis')}</span>
          </h1>

          <p className="mx-auto mt-6 max-w-xl text-pretty text-lg leading-relaxed text-muted-foreground sm:text-xl">
            {t('hero.desc')}
          </p>

          <div className="mt-10 flex flex-col items-center justify-center gap-5 sm:flex-row">
            <Button
              asChild
              size="lg"
              className={`h-12 rounded-full bg-green-700 px-8 text-base shadow-sm transition-transform duration-200 hover:bg-green-800 active:scale-[0.97] ${APPLE_EASE}`}
            >
              <Link href="/recommendation">{t('hero.btns.recommendation')}</Link>
            </Button>
            <Link
              href="/disease-detection"
              className="group inline-flex items-center gap-0.5 text-base font-medium text-green-700 transition-colors hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
            >
              {t('hero.btns.disease')}
              <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
            </Link>
          </div>

          <div className="mt-12 flex items-center justify-center gap-8 text-sm text-muted-foreground">
            <div className="tabular">
              <span className="text-2xl font-semibold tracking-tight text-foreground">95%</span>
              <span className="ml-1.5">{t('hero.kpis.accuracy')}</span>
            </div>
            <span aria-hidden className="h-1 w-1 rounded-full bg-muted-foreground/40" />
            <div className="tabular">
              <span className="text-2xl font-semibold tracking-tight text-foreground">75+</span>
              <span className="ml-1.5">{t('hero.kpis.crops')}</span>
            </div>
          </div>
        </div>

        {/* Floating product card — mirrors the real recommendation output */}
        <div
          ref={panelRef}
          className={`mx-auto mt-16 max-w-2xl transition-all delay-150 duration-700 sm:mt-20 ${APPLE_EASE} ${
            panelVisible ? 'translate-y-0 opacity-100' : 'translate-y-6 opacity-0'
          }`}
        >
          <div className="rounded-[28px] border border-black/[0.06] bg-card shadow-[0_24px_60px_-24px_rgba(0,0,0,0.18)] dark:border-white/[0.08] dark:shadow-[0_24px_60px_-24px_rgba(0,0,0,0.6)]">
            <div className="flex items-center justify-between border-b border-border/60 px-7 py-4">
              <span className="text-xs font-medium tracking-[0.12em] text-muted-foreground">
                {t('hero.panel.eyebrow')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-green-700 dark:text-green-400">
                <span className="h-1.5 w-1.5 rounded-full bg-green-600" aria-hidden />
                {t('hero.panel.status')}
              </span>
            </div>

            <div className="px-7 py-6">
              <div className="mb-4 text-[11px] font-medium tracking-[0.12em] text-muted-foreground">
                {t('hero.panel.fieldsTitle')}
              </div>
              <dl className="grid grid-cols-2 gap-x-6 gap-y-3 sm:grid-cols-3">
                {panelFields.map((field, i) => (
                  <div key={i}>
                    <dt className="text-xs text-muted-foreground">{field.label}</dt>
                    <dd className="tabular mt-0.5 text-sm font-semibold text-foreground">
                      {field.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="rounded-b-[28px] bg-muted/40 px-7 py-6 dark:bg-white/[0.03]">
              <div className="mb-4 text-[11px] font-medium tracking-[0.12em] text-muted-foreground">
                {t('hero.panel.resultTitle')}
              </div>
              <div className="space-y-3">
                {panelCrops.map((crop, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <span className="w-10 shrink-0 text-sm font-medium text-foreground">
                      {crop.name}
                    </span>
                    <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-foreground/10">
                      <div
                        className="h-full rounded-full bg-green-600 transition-[width] duration-1000 dark:bg-green-500"
                        style={{ width: panelVisible ? `${crop.confidence}%` : '0%' }}
                      />
                    </div>
                    <span className="tabular w-9 shrink-0 text-right text-xs font-semibold text-muted-foreground">
                      {crop.confidence}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
